import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import {
  NC_SUPPLIER_COMPLIANCE_DATA,
  SupplierComplianceArticle,
  SupplierComplianceBucket,
  SupplierComplianceNc,
} from "../../models/data.model";
import {
  QUALITE_C,
  colLetter,
  downloadXlsx,
  qualiteHeaderStyle,
  qualiteRowStyle,
  qualiteSectionTitle,
  qualiteStatLabelStyle,
  qualiteStatStyle,
  qualiteSubHeaderStyle,
  todayIso,
} from "../qualite-excel-shared.utils";

const HEADERS = [
  "Fournisseur",
  "Taux conformité (%)",
  "NC",
  "NC critiques",
  "Réceptions",
  "Qty conforme (UG)",
  "Qty non conforme (UG)",
  "Coût NC (€)",
];
const COL_WIDTHS = [32, 18, 10, 14, 14, 18, 22, 16];

function tauxColor(taux: number | null): string {
  if (taux == null) return QUALITE_C.origineNonRenseigne;
  if (taux < 90) return QUALITE_C.severityCritique;
  if (taux < 95) return QUALITE_C.severityMajeure;
  return QUALITE_C.severityMineure;
}

/**
 * Retourne la date au format yyyy-MM-dd. Tolère string (ISO ou day) ou Date —
 * la sérialisation JSON peut renvoyer un objet Date selon le pipeline.
 */
function formatIsoDay(value: string | Date | null | undefined): string {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  // Fallback : tente une conversion
  try {
    return new Date(value as any).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function writeCoverSheet(wb: ExcelJS.Workbook, data: NC_SUPPLIER_COMPLIANCE_DATA): void {
  const ws = wb.addWorksheet("Synthèse", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { tabColor: { argb: QUALITE_C.headerBg } },
  });
  [22, 18, 18, 18, 18, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:F1");
  qualiteSectionTitle(ws.getCell("A1"), "Conformité fournisseurs — Top pires & meilleurs");
  ws.getRow(1).height = 32;

  ws.mergeCells("A2:F2");
  const sub = ws.getCell("A2");
  sub.value = `Période : ${data.start} → ${data.end} — Document généré par JustUp`;
  sub.font = { size: 10, italic: true, color: { argb: "FF667085" } };

  // KPI synthétiques
  const totalFournisseurs = (data.worst.length + data.best.length);
  const moyTauxBest =
    data.best.length > 0
      ? data.best.reduce((s, b) => s + (b.tauxConformite ?? 0), 0) / data.best.length
      : null;
  const moyTauxWorst =
    data.worst.length > 0
      ? data.worst.reduce((s, b) => s + (b.tauxConformite ?? 0), 0) / data.worst.length
      : null;
  const totalNc = [...data.worst, ...data.best].reduce((s, b) => s + b.nbNc, 0);
  const totalCritiques = [...data.worst, ...data.best].reduce(
    (s, b) => s + b.nbNcCritiques,
    0,
  );

  const cards: { label: string; value: string | number; bg: string }[] = [
    { label: "Fournisseurs analysés", value: totalFournisseurs, bg: QUALITE_C.statBlue },
    {
      label: "Taux moyen — Top",
      value: moyTauxBest == null ? "—" : Math.round(moyTauxBest * 10) / 10,
      bg: QUALITE_C.statGreen,
    },
    {
      label: "Taux moyen — Worst",
      value: moyTauxWorst == null ? "—" : Math.round(moyTauxWorst * 10) / 10,
      bg: QUALITE_C.statRed,
    },
    { label: "Nombre total NC", value: totalNc, bg: QUALITE_C.statPurple },
    {
      label: "Dont critiques",
      value: totalCritiques,
      bg: QUALITE_C.statOrange,
    },
  ];
  cards.forEach((c, i) => {
    const labelCell = ws.getCell(4, i + 1);
    labelCell.value = c.label;
    qualiteStatLabelStyle(labelCell, c.bg);
    const valCell = ws.getCell(5, i + 1);
    valCell.value = c.value;
    qualiteStatStyle(valCell, c.bg);
    if (typeof c.value === "number") valCell.numFmt = "#,##0.##";
  });
  ws.getRow(4).height = 18;
  ws.getRow(5).height = 28;

  // Méthodologie
  ws.mergeCells("A8:F8");
  qualiteSectionTitle(ws.getCell("A8"), "Méthodologie (KPI IFS 4.4)");
  ws.getRow(8).height = 22;

  const notes = [
    `Taux de conformité = qty conforme / (qty conforme + qty non conforme), agrégé sur les réceptions de la période.`,
    `Conversion en unité de gestion (UG) via le coefficient de la ligne de commande.`,
    `Croisement avec les NC type RECEPTION pour quantifier les défauts détectés.`,
    `Code couleur : rouge < 90%, orange < 95%, vert ≥ 95%.`,
  ];
  let r = 10;
  for (const note of notes) {
    ws.mergeCells(r, 1, r, 6);
    const c = ws.getCell(r, 1);
    c.value = `• ${note}`;
    qualiteRowStyle(c, r % 2 === 0);
    c.alignment = { vertical: "middle", indent: 1 };
    r += 1;
  }
}

function writeBucketSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  buckets: SupplierComplianceBucket[],
  period: { start: string; end: string },
  tabColor: string,
): void {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 5 }],
    properties: { tabColor: { argb: tabColor } },
  });
  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:H1");
  qualiteSectionTitle(ws.getCell("A1"), `${sheetName} — ${period.start} → ${period.end}`);
  ws.getRow(1).height = 28;

  const headerRow = 5;
  HEADERS.forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: HEADERS.length },
  };

  let r = headerRow + 1;
  for (const b of buckets) {
    const cells: (string | number | null)[] = [
      b.denomination,
      b.tauxConformite == null ? null : b.tauxConformite,
      b.nbNc,
      b.nbNcCritiques,
      b.nbReceptions,
      b.qtyConformeUG,
      b.qtyNonConformeUG,
      b.coutNc,
    ];
    cells.forEach((v, i) => {
      const c = ws.getCell(r, i + 1);
      c.value = v as any;
      qualiteRowStyle(c, r % 2 === 0);
      if (i === 0) {
        c.font = { ...c.font, bold: true };
      } else {
        c.alignment = { horizontal: "right" };
        if (i === 1 && typeof v === "number") {
          c.numFmt = "#,##0.0";
          c.font = { ...c.font, bold: true, color: { argb: tauxColor(v as number) } };
        } else if (i === 7) {
          c.numFmt = "#,##0.00";
        } else if (i === 5 || i === 6) {
          c.numFmt = "#,##0.##";
        } else {
          c.numFmt = "#,##0";
        }
      }
    });
    r += 1;
  }

  if (buckets.length > 0) {
    const tr = r + 1;
    const a = ws.getCell(tr, 1);
    a.value = "TOTAL";
    qualiteSubHeaderStyle(a);
    a.alignment = { horizontal: "right" };
    [3, 4, 5, 6, 7, 8].forEach((col) => {
      const c = ws.getCell(tr, col);
      c.value = {
        formula: `SUM(${colLetter(col)}${headerRow + 1}:${colLetter(col)}${r - 1})`,
      } as any;
      qualiteSubHeaderStyle(c);
      c.alignment = { horizontal: "right" };
      c.numFmt = col === 8 ? "#,##0.00" : col === 6 || col === 7 ? "#,##0.##" : "#,##0";
    });
  }
}

/**
 * Feuille "Tous les fournisseurs" — liste exhaustive (mode detailed).
 */
function writeAllSuppliersSheet(
  wb: ExcelJS.Workbook,
  buckets: SupplierComplianceBucket[],
  period: { start: string; end: string },
): void {
  const ws = wb.addWorksheet("Tous les fournisseurs", {
    views: [{ state: "frozen", ySplit: 5 }],
    properties: { tabColor: { argb: QUALITE_C.headerBg } },
  });
  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:H1");
  qualiteSectionTitle(
    ws.getCell("A1"),
    `Tous les fournisseurs — ${period.start} → ${period.end}`,
  );
  ws.getRow(1).height = 28;

  const headerRow = 5;
  HEADERS.forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: HEADERS.length },
  };

  let r = headerRow + 1;
  for (const b of buckets) {
    const cells: (string | number | null)[] = [
      b.denomination,
      b.tauxConformite == null ? null : b.tauxConformite,
      b.nbNc,
      b.nbNcCritiques,
      b.nbReceptions,
      b.qtyConformeUG,
      b.qtyNonConformeUG,
      b.coutNc,
    ];
    cells.forEach((v, i) => {
      const c = ws.getCell(r, i + 1);
      c.value = v as any;
      qualiteRowStyle(c, r % 2 === 0);
      if (i === 0) {
        c.font = { ...c.font, bold: true };
      } else {
        c.alignment = { horizontal: "right" };
        if (i === 1 && typeof v === "number") {
          c.numFmt = "#,##0.0";
          c.font = { ...c.font, bold: true, color: { argb: tauxColor(v as number) } };
        } else if (i === 7) {
          c.numFmt = "#,##0.00";
        } else if (i === 5 || i === 6) {
          c.numFmt = "#,##0.##";
        } else {
          c.numFmt = "#,##0";
        }
      }
    });
    r += 1;
  }

  if (buckets.length > 0) {
    const tr = r + 1;
    const a = ws.getCell(tr, 1);
    a.value = "TOTAL";
    qualiteSubHeaderStyle(a);
    a.alignment = { horizontal: "right" };
    [3, 4, 5, 6, 7, 8].forEach((col) => {
      const c = ws.getCell(tr, col);
      c.value = {
        formula: `SUM(${colLetter(col)}${headerRow + 1}:${colLetter(col)}${r - 1})`,
      } as any;
      qualiteSubHeaderStyle(c);
      c.alignment = { horizontal: "right" };
      c.numFmt = col === 8 ? "#,##0.00" : col === 6 || col === 7 ? "#,##0.##" : "#,##0";
    });
  }
}

/**
 * Feuille "Détail produits reçus" — par fournisseur, avec articles.
 */
function writeArticlesSheet(
  wb: ExcelJS.Workbook,
  data: NC_SUPPLIER_COMPLIANCE_DATA,
): void {
  const articlesByTiers = data.articlesByTiers ?? {};
  const buckets = data.all ?? [...data.worst, ...data.best];

  const ws = wb.addWorksheet("Détail produits", {
    views: [{ state: "frozen", ySplit: 5 }],
    properties: { tabColor: { argb: QUALITE_C.statBlue } },
  });

  const widths = [32, 14, 32, 18, 14, 16, 16, 18];
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:H1");
  qualiteSectionTitle(
    ws.getCell("A1"),
    `Détail produits reçus — ${data.start} → ${data.end}`,
  );
  ws.getRow(1).height = 28;

  const headers = [
    "Fournisseur",
    "Code article",
    "Article",
    "Unité gestion",
    "Réceptions",
    "Qty conforme",
    "Qty non conforme",
    "CA réception (€)",
  ];
  const headerRow = 5;
  headers.forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: headers.length },
  };

  let r = headerRow + 1;
  for (const b of buckets) {
    const articles = articlesByTiers[b.tiersId];
    if (!articles?.length) continue;
    for (const a of articles) {
      const cells: (string | number | null)[] = [
        b.denomination,
        a.code ?? "",
        a.denomination ?? "—",
        a.uniteGestion ?? "",
        a.nbReceptions,
        a.qtyConformeUG,
        a.qtyNonConformeUG,
        a.caTotal,
      ];
      cells.forEach((v, i) => {
        const c = ws.getCell(r, i + 1);
        c.value = v as any;
        qualiteRowStyle(c, r % 2 === 0);
        if (i >= 4) c.alignment = { horizontal: "right" };
        if (i === 4) c.numFmt = "#,##0";
        else if (i === 5 || i === 6) c.numFmt = "#,##0.##";
        else if (i === 7) c.numFmt = "#,##0.00";
      });
      r += 1;
    }
  }
}

/**
 * Feuille "Détail NC réception" — toutes les NC type RECEPTION par fournisseur.
 */
function writeNcDetailSheet(
  wb: ExcelJS.Workbook,
  data: NC_SUPPLIER_COMPLIANCE_DATA,
): void {
  const ncByTiers = data.ncByTiers ?? {};
  const buckets = data.all ?? [...data.worst, ...data.best];
  const denomByTiers = new Map<string, string>(
    buckets.map((b) => [b.tiersId, b.denomination]),
  );

  const ws = wb.addWorksheet("Détail NC réception", {
    views: [{ state: "frozen", ySplit: 5 }],
    properties: { tabColor: { argb: QUALITE_C.severityCritique } },
  });

  const widths = [28, 12, 12, 12, 22, 14, 14, 14, 14, 12, 14, 14, 16, 16, 50];
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:O1");
  qualiteSectionTitle(
    ws.getCell("A1"),
    `Détail NC type RECEPTION — ${data.start} → ${data.end}`,
  );
  ws.getRow(1).height = 28;

  const headers = [
    "Fournisseur",
    "N° NC",
    "Date",
    "Gravité",
    "Nature",
    "Cause 5M",
    "État",
    "Décision",
    "Coût estimé (€)",
    "Quantité",
    "Lot",
    "Palette",
    "Commande F.",
    "Article",
    "Description",
  ];
  const headerRow = 5;
  headers.forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: headers.length },
  };

  let r = headerRow + 1;
  const tiersIds = Object.keys(ncByTiers);
  for (const tid of tiersIds) {
    const ncs = ncByTiers[tid];
    if (!ncs?.length) continue;
    for (const nc of ncs) {
      const cells: (string | number | null)[] = [
        denomByTiers.get(tid) ?? "(tiers inconnu)",
        nc.numero ?? "",
        formatIsoDay(nc.dateOuverture),
        nc.gravite ?? "",
        nc.natureLabel ?? "",
        nc.cause5M ?? "",
        nc.etat ?? "",
        nc.decision ?? "",
        nc.coutEstime,
        nc.quantiteConcernee,
        nc.lotNumero ?? "",
        nc.paletteSscc ?? "",
        nc.commandeFournisseurNumero ?? "",
        nc.articleDenomination ?? "",
        nc.description ?? "",
      ];
      cells.forEach((v, i) => {
        const c = ws.getCell(r, i + 1);
        c.value = v as any;
        qualiteRowStyle(c, r % 2 === 0);
        if (i === 8 && typeof v === "number") {
          c.alignment = { horizontal: "right" };
          c.numFmt = "#,##0.00";
        } else if (i === 9 && typeof v === "number") {
          c.alignment = { horizontal: "right" };
          c.numFmt = "#,##0.##";
        }
      });
      r += 1;
    }
  }
}

export async function exportNcSupplierComplianceExcel(
  data: NC_SUPPLIER_COMPLIANCE_DATA,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Conformité fournisseurs — Top NC réception",
    subject: "Indicateur IFS 4.4 — Maîtrise des achats",
    description:
      "Classement des fournisseurs sur la période : taux de conformité observé sur les réceptions, " +
      "nombre de NC type RECEPTION, NC critiques et coût estimé. Top pires et meilleurs + détail produits + détail NC.",
    keywords: "JustUp, IFS, fournisseurs, conformité, NC, achats",
  });

  const period = { start: data.start, end: data.end };
  writeCoverSheet(wb, data);

  // Liste exhaustive (mode detailed). Si pas dispo, on retombe sur worst+best agrégés.
  const allSuppliers = data.all && data.all.length > 0
    ? data.all
    : (() => {
        const map = new Map<string, SupplierComplianceBucket>();
        for (const b of [...data.worst, ...data.best]) {
          if (!map.has(b.tiersId)) map.set(b.tiersId, b);
        }
        return Array.from(map.values());
      })();
  if (allSuppliers.length > 0) {
    writeAllSuppliersSheet(wb, allSuppliers, period);
  }

  if (data.worst?.length) {
    writeBucketSheet(wb, "Pires fournisseurs", data.worst, period, QUALITE_C.severityCritique);
  }
  if (data.best?.length) {
    writeBucketSheet(
      wb,
      "Meilleurs fournisseurs",
      data.best,
      period,
      QUALITE_C.severityMineure,
    );
  }

  // Détail produits reçus (mode detailed uniquement)
  if (data.articlesByTiers && Object.keys(data.articlesByTiers).length > 0) {
    writeArticlesSheet(wb, data);
  }

  // Détail NC type RECEPTION (mode detailed uniquement)
  if (data.ncByTiers && Object.keys(data.ncByTiers).length > 0) {
    writeNcDetailSheet(wb, data);
  }

  for (const ws of wb.worksheets) {
    const lastCol = Math.max(8, ws.actualColumnCount || 8);
    await addJustupBranding(wb, ws, lastCol);
  }

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  downloadXlsx(buffer, `nc-conformite-fournisseurs-${todayIso()}.xlsx`);
}
